import React, { useState, useEffect } from "react";
import * as api from "../../services/api";
import "./CouponsManagement.css";

export default function CouponsManagement() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discountType: "percentage",
    discountValue: "",
    minPurchaseAmount: "",
    maxDiscountAmount: "",
    validFrom: "",
    validUntil: "",
    usageLimit: "",
    isActive: true
  });

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const couponsData = await api.getAllCoupons();
      setCoupons(couponsData);
    } catch (error) {
      console.error('Error loading coupons:', error);
      setError('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.code.trim()) {
      setError("Coupon code is required");
      return;
    }
    if (!formData.discountValue || parseFloat(formData.discountValue) <= 0) {
      setError("Discount value must be greater than 0");
      return;
    }
    if (!formData.validUntil) {
      setError("Expiry date is required");
      return;
    }

    try {
      const couponData = {
        code: formData.code.toUpperCase().trim(),
        description: formData.description.trim(),
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue),
        minPurchaseAmount: parseFloat(formData.minPurchaseAmount) || 0,
        maxDiscountAmount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : null,
        validFrom: formData.validFrom ? new Date(formData.validFrom) : new Date(),
        validUntil: new Date(formData.validUntil),
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
        isActive: formData.isActive
      };

      if (editingCoupon) {
        await api.updateCoupon(editingCoupon._id, couponData);
      } else {
        await api.createCoupon(couponData);
      }

      resetForm();
      loadCoupons();
    } catch (error) {
      console.error('Error saving coupon:', error);
      setError(error.response?.data?.message || 'Failed to save coupon');
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      description: coupon.description || "",
      discountType: coupon.discountType || "percentage",
      discountValue: coupon.discountValue || "",
      minPurchaseAmount: coupon.minPurchaseAmount || "",
      maxDiscountAmount: coupon.maxDiscountAmount || "",
      validFrom: coupon.validFrom ? new Date(coupon.validFrom).toISOString().split('T')[0] : "",
      validUntil: coupon.validUntil ? new Date(coupon.validUntil).toISOString().split('T')[0] : "",
      usageLimit: coupon.usageLimit || "",
      isActive: coupon.isActive !== undefined ? coupon.isActive : true
    });
    setShowAddModal(true);
    setError("");
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    try {
      await api.deleteCoupon(id);
      loadCoupons();
    } catch (error) {
      console.error('Error deleting coupon:', error);
      alert(error.response?.data?.message || 'Failed to delete coupon');
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await api.updateCoupon(coupon._id, { isActive: !coupon.isActive });
      loadCoupons();
    } catch (error) {
      console.error('Error toggling coupon status:', error);
      alert(error.response?.data?.message || 'Failed to update coupon status');
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      description: "",
      discountType: "percentage",
      discountValue: "",
      minPurchaseAmount: "",
      maxDiscountAmount: "",
      validFrom: "",
      validUntil: "",
      usageLimit: "",
      isActive: true
    });
    setEditingCoupon(null);
    setShowAddModal(false);
    setError("");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const isExpired = (validUntil) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  const getStatusBadge = (coupon) => {
    if (!coupon.isActive) {
      return <span className="status-badge inactive">Inactive</span>;
    }
    if (isExpired(coupon.validUntil)) {
      return <span className="status-badge expired">Expired</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  if (loading) {
    return <div className="coupons-loading">Loading coupons...</div>;
  }

  return (
    <div className="coupons-management">
      <div className="coupons-header">
        <h1>Coupons Management</h1>
        <button className="btn primary" onClick={() => {
          resetForm();
          setShowAddModal(true);
        }}>
          + Add New Coupon
        </button>
      </div>

      {error && !showAddModal && <div className="error-message">{error}</div>}

      <div className="coupons-table-container">
        {coupons.length === 0 ? (
          <div className="empty-state">
            <p>No coupons found. Click "Add New Coupon" to get started.</p>
          </div>
        ) : (
          <table className="coupons-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
                <th>Discount</th>
                <th>Min Amount</th>
                <th>Valid Until</th>
                <th>Status</th>
                <th>Used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map((coupon) => (
                <tr key={coupon._id}>
                  <td className="coupon-code">{coupon.code}</td>
                  <td>{coupon.description || "-"}</td>
                  <td>
                    {coupon.discountType === 'percentage' 
                      ? `${coupon.discountValue}%`
                      : `₹${coupon.discountValue}`
                    }
                    {coupon.maxDiscountAmount && coupon.discountType === 'percentage' && (
                      <span className="max-discount"> (max ₹{coupon.maxDiscountAmount})</span>
                    )}
                  </td>
                  <td>₹{coupon.minPurchaseAmount || 0}</td>
                  <td className={isExpired(coupon.validUntil) ? 'expired-date' : ''}>
                    {formatDate(coupon.validUntil)}
                  </td>
                  <td>{getStatusBadge(coupon)}</td>
                  <td>
                    {coupon.usageLimit 
                      ? `${coupon.usedCount || 0} / ${coupon.usageLimit}`
                      : `${coupon.usedCount || 0}`
                    }
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className={`btn-toggle ${coupon.isActive ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleActive(coupon)}
                        title={coupon.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {coupon.isActive ? '✓' : '✗'}
                      </button>
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(coupon)}
                        title="Edit Coupon"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(coupon._id)}
                        title="Delete Coupon"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="coupons-mobile-view">
        {coupons.length === 0 ? (
          <div className="empty-state">
            <p>No coupons found. Click "Add New Coupon" to get started.</p>
          </div>
        ) : (
          coupons.map((coupon) => (
            <div key={coupon._id} className="coupon-mobile-card">
              <div className="coupon-card-header">
                <h3>{coupon.code}</h3>
                {getStatusBadge(coupon)}
              </div>
              <div className="coupon-card-info">
                <div className="info-item">
                  <span className="info-label">Description:</span>
                  <span className="info-value">{coupon.description || "-"}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Discount:</span>
                  <span className="info-value">
                    {coupon.discountType === 'percentage' 
                      ? `${coupon.discountValue}%`
                      : `₹${coupon.discountValue}`
                    }
                    {coupon.maxDiscountAmount && coupon.discountType === 'percentage' && (
                      <span> (max ₹{coupon.maxDiscountAmount})</span>
                    )}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Min Amount:</span>
                  <span className="info-value">₹{coupon.minPurchaseAmount || 0}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Valid Until:</span>
                  <span className={`info-value ${isExpired(coupon.validUntil) ? 'expired-date' : ''}`}>
                    {formatDate(coupon.validUntil)}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Used:</span>
                  <span className="info-value">
                    {coupon.usageLimit 
                      ? `${coupon.usedCount || 0} / ${coupon.usageLimit}`
                      : `${coupon.usedCount || 0}`
                    }
                  </span>
                </div>
              </div>
              <div className="coupon-card-actions">
                <button
                  className={`btn-toggle ${coupon.isActive ? 'active' : 'inactive'}`}
                  onClick={() => handleToggleActive(coupon)}
                >
                  {coupon.isActive ? '✓ Deactivate' : '✗ Activate'}
                </button>
                <button
                  className="btn-edit"
                  onClick={() => handleEdit(coupon)}
                >
                  Edit
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDelete(coupon._id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCoupon ? 'Edit Coupon' : 'Add New Coupon'}</h2>
              <button className="modal-close" onClick={resetForm}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="coupon-form">
              {error && <div className="error-message">{error}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label style={{color: 'black'}}>Coupon Code *</label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    placeholder="e.g., SAVE20"
                    required
                    disabled={!!editingCoupon}
                  />
                  {editingCoupon && (
                    <small style={{color: '#666', fontSize: '0.85rem'}}>Code cannot be changed</small>
                  )}
                </div>
                <div className="form-group">
                  <label style={{color: 'black'}}>Discount Type *</label>
                  <select
                    name="discountType"
                    value={formData.discountType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label style={{color: 'black'}}>Discount Value *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    name="discountValue"
                    value={formData.discountValue}
                    onChange={handleInputChange}
                    placeholder={formData.discountType === 'percentage' ? "20" : "100"}
                    required
                  />
                </div>
                {formData.discountType === 'percentage' && (
                  <div className="form-group">
                    <label style={{color: 'black'}}>Max Discount Amount (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      name="maxDiscountAmount"
                      value={formData.maxDiscountAmount}
                      onChange={handleInputChange}
                      placeholder="Optional"
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label style={{color: 'black'}}>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Coupon description (optional)"
                  rows="2"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label style={{color: 'black'}}>Minimum Purchase Amount (₹) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    name="minPurchaseAmount"
                    value={formData.minPurchaseAmount}
                    onChange={handleInputChange}
                    placeholder="0"
                    required
                  />
                </div>
                <div className="form-group">
                  <label style={{color: 'black'}}>Usage Limit</label>
                  <input
                    type="number"
                    min="1"
                    name="usageLimit"
                    value={formData.usageLimit}
                    onChange={handleInputChange}
                    placeholder="Leave empty for unlimited"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label style={{color: 'black'}}>Valid From</label>
                  <input
                    type="date"
                    name="validFrom"
                    value={formData.validFrom}
                    onChange={handleInputChange}
                  />
                  <small style={{color: '#666', fontSize: '0.85rem'}}>Leave empty to use current date</small>
                </div>
                <div className="form-group">
                  <label style={{color: 'black'}}>Valid Until *</label>
                  <input
                    type="date"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={handleInputChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div className="form-group">
                <label style={{color: 'black', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Active (Coupon is currently active)
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="btn primary">
                  {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}



